/**
 * @fileoverview The page key: one random secret per private page, and the only
 * thing standing between a private page and anybody who asks for it.
 *
 * THIS IS THE FOUNDATION THE WHOLE PRIVATE PATH SHARES. Three separate things
 * are encrypted for a private page and all three use the key made here:
 *
 *   the body        text, uploaded to Blossom as ciphertext
 *   the event       everything except the `d` tag, carried in `.content`
 *   the key itself  wrapped to each recipient, on a share event
 *
 * Only the last of those involves the author's own Nostr key, and it is
 * deliberately the only one, because it is the only one a remote signer has to
 * be asked about. A reader who already holds the page key decrypts the body and
 * the event with no signer round trip at all.
 *
 * WHY THE PROTECTION IS THE KEY AND NOT A PERMISSIONS LIST. `cloistr-blossom`
 * registers `GET /:hash` with no auth middleware, per the Blossom spec, so a
 * stranger holding the hash gets the bytes. A server-side list in front of that
 * would protect nothing while convincing users it did. The bytes are ciphertext
 * instead, which is a claim that survives our servers being wrong, borrowed, or
 * gone. "Explicitly granted permission" means holding this key and nothing else.
 *
 * ---
 *
 * THE KEY IS RANDOM AND IT IS NEVER DERIVED. Operator ruling, and the file
 * service does the opposite -- Stash derives a file key from the folder and file
 * ids under a root key (`keys.ts`, `deriveFileKey`). Copying that here would
 * have been the obvious move and it is wrong for pages, for a reason specific to
 * how a page is addressed:
 *
 * A page's address is PERMANENT. That is the central decision of the whole
 * design; every shared link depends on it. So anything derived from a page's
 * identity is permanent too, and revocation -- which we promise covers future
 * versions -- means the next version must be encrypted under a key the removed
 * recipient does not have. A derived key cannot change without the page
 * changing its name, and a page changing its name is a page being abandoned.
 * Derivation would therefore have made the one privacy promise we do make
 * impossible to keep, and it would have taken a schema change to find out.
 *
 * A random key also confines a compromise to one page. A derived scheme spreads
 * from the root: whoever reconstructs the derivation reads everything under it.
 *
 * ---
 *
 * WHY NIP-44 RATHER THAN THE FILE SERVICE'S XCHACHA20-POLY1305 CONTAINER.
 * Stash's format is XChaCha20-Poly1305 with a `nonce||ciphertext||tag` layout
 * over libsodium, and it is backward-compatibility critical for blobs users
 * already have. We hold none of those blobs, so nothing forces us onto it, and
 * two things argue against:
 *
 * 1. THE EVENT HAS TO BE NIP-44 ANYWAY. An event's `.content` is a string and
 *    the wrapped key is a string, so those two are NIP-44 whatever we choose.
 *    Encrypting the body with something else would mean two crypto formats and
 *    two failure modes for one page.
 * 2. THE READING APP STAYS LIGHT. libsodium ships a WebAssembly module; NIP-44
 *    is already present, because `nostr-tools` is how we talk to the relay at
 *    all. The main screen of this app is a PUBLIC page read by someone with no
 *    account, and making that person download a crypto runtime for a feature
 *    they are not using is the wrong way round.
 *
 * The cost is honest and it is base64: ciphertext lands about a third larger
 * than the plaintext. That is paid on a Blossom blob, which has no size ceiling
 * of ours to run into, rather than on the event, which does.
 *
 * ---
 *
 * USING A RANDOM KEY AS A NIP-44 CONVERSATION KEY IS SOUND, and it is worth
 * saying why, because it looks like a misuse. NIP-44's conversation key is not
 * a password or a shared secret in the loose sense: it is the output of
 * HKDF-Extract over an ECDH shared point, i.e. a pseudorandom key, and
 * `encrypt` immediately runs HKDF-Expand over it with a fresh 32-byte nonce to
 * get the actual ChaCha20 key, nonce and HMAC key. Thirty-two uniformly random
 * bytes are exactly what Expand wants as a PRK -- strictly a better one than an
 * Extract output, which merely approximates uniform. Nothing in the format
 * binds the conversation key to a pair of Nostr keys.
 *
 * VERIFIED AGAINST THE SPEC RATHER THAN MEMORY, 2026-09-15, because the obvious
 * worry is a size cap: NIP-44 v2 pads with a 2-byte length prefix below 65,536
 * bytes and a 6-byte prefix at or above it, up to 4,294,967,295. The extended
 * prefix is in the spec (NIP-44, "Add padding", and the boundary test vectors at
 * 65535/65536), not a library extension, so a page body larger than 64 KiB is
 * ordinary NIP-44 and not something only our own copy of nostr-tools can read.
 *
 * A PAGE BODY IS ALWAYS TEXT -- markdown or raw HTML, those two and no third --
 * so NIP-44's string interface costs us nothing. There is deliberately no
 * byte-oriented variant here. Adding one would be the moment a page could hold
 * something that is not a page.
 */

import { nip44 } from 'nostr-tools';

/**
 * How many bytes a page key is.
 *
 * Thirty-two, because that is what NIP-44 expands from. Unlike the sixteen hex
 * characters of a page's NAME, this one IS a security parameter: the name only
 * has to say nothing, while this has to resist being guessed by anyone who can
 * already fetch the ciphertext, which per the Blossom spec is everyone.
 */
export const PAGE_KEY_BYTES = 32;

/** A page's secret. Hold it and you can read the page; that is the whole model. */
export type PageKey = Uint8Array;

/**
 * A fresh key for a page that does not have one.
 *
 * `crypto.getRandomValues` and nothing else. There is no seeding argument, no
 * derivation argument and no "make it reproducible for tests" argument: a test
 * that needs a fixed key constructs the bytes itself, which is why
 * `pageKeyFromHex` exists below and is exported.
 */
export function generatePageKey(): PageKey {
  return crypto.getRandomValues(new Uint8Array(PAGE_KEY_BYTES));
}

/**
 * Encrypt text under a page key.
 *
 * Used for the body and for the event's hidden tags alike. The caller decides
 * what the text MEANS; this deliberately knows nothing about pages, so that the
 * one place the key is applied is the one place to audit.
 */
export function encryptWithPageKey(plaintext: string, key: PageKey): string {
  assertKeyShape(key);
  return nip44.v2.encrypt(plaintext, key);
}

/**
 * Decrypt text under a page key, or throw.
 *
 * THROWING IS THE CONTRACT AND CALLERS MUST NOT SOFTEN IT INTO A NULL. NIP-44
 * verifies an HMAC before it returns anything, so a failure here means the
 * payload was truncated, corrupted, or encrypted to a different key -- never
 * "probably fine". A caller that swallowed this would render whatever it had,
 * and what it had would be nothing.
 *
 * The honest no-access page is built from this throwing, at the one level that
 * knows whether the reader was ever supposed to have the key.
 */
export function decryptWithPageKey(payload: string, key: PageKey): string {
  assertKeyShape(key);
  return nip44.v2.decrypt(payload, key);
}

/**
 * A page key as lowercase hex, for travelling inside a share payload.
 *
 * Hex rather than base64 because this value is read by humans during support
 * and by us in proofs, and because every other key-shaped thing in Nostr is
 * hex. The extra eight bytes on the wire are not worth a second encoding to
 * hold in your head.
 */
export function pageKeyToHex(key: PageKey): string {
  assertKeyShape(key);
  return Array.from(key, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Read a page key back from hex, refusing anything that is not one.
 *
 * REFUSING IS THE POINT. This parses a value that arrived from OUTSIDE -- out of
 * a share event somebody else signed -- so "64 hex characters" is a claim to
 * check rather than a shape to assume. A short or malformed key that slipped
 * through would fail later inside NIP-44 with a message about HKDF, which is
 * the sort of error nobody can act on.
 */
export function pageKeyFromHex(hex: string): PageKey {
  if (!/^[0-9a-f]{64}$/i.test(hex)) {
    throw new Error('Not a page key: expected 64 hex characters');
  }
  const key = new Uint8Array(PAGE_KEY_BYTES);
  for (let i = 0; i < PAGE_KEY_BYTES; i++) {
    key[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return key;
}

/**
 * Refuse a key of the wrong size before NIP-44 sees it.
 *
 * Not defensive clutter. `hkdf_expand` accepts a short PRK and produces a
 * weaker key from it without complaint, so a truncated key would encrypt
 * successfully, decrypt successfully, and be wrong in the only way that
 * matters. This is the check that turns that into a stack trace.
 */
function assertKeyShape(key: PageKey): void {
  if (key.length !== PAGE_KEY_BYTES) {
    throw new Error(`Not a page key: expected ${PAGE_KEY_BYTES} bytes, got ${key.length}`);
  }
}
